
/**
 * Calculate portfolio valuation from tenements with resource estimations
 * @param tenements - Array of tenements with resource estimations
 * @returns Total market value of all resources
 */
export function calculatePortfolioValue(tenements: any[]): number {
  return tenements.reduce((total, tenement) => {
    if (tenement.resourceEstimation?.marketValue) {
      return total + tenement.resourceEstimation.marketValue;
    }
    return total;
  }, 0);
}

/**
 * Group resource values by mineral type
 * @param tenements - Array of tenements with resource estimations
 * @returns Object with mineral types as keys and their total values
 */
export function groupResourcesByMineral(tenements: any[]): { [key: string]: number } {
  return tenements.reduce((result: { [key: string]: number }, tenement) => {
    if (tenement.resourceEstimation?.marketValue) {
      const mineralType = tenement.resourceEstimation.mineralType;
      result[mineralType] = (result[mineralType] || 0) + tenement.resourceEstimation.marketValue;
    }
    return result;
  }, {});
}

/**
 * Group resource values by classification
 * @param tenements - Array of tenements with resource estimations
 * @returns Object with classifications as keys and their total values
 */
export function groupResourcesByClassification(tenements: any[]): { [key: string]: number } {
  return tenements.reduce((result: { [key: string]: number }, tenement) => {
    if (tenement.resourceEstimation?.marketValue) {
      const classification = tenement.resourceEstimation.classification;
      result[classification] = (result[classification] || 0) + tenement.resourceEstimation.marketValue;
    }
    return result;
  }, {});
}

/**
 * Calculate the total estimated resource value
 * @param tenement - Tenement object with resource data
 * @returns The total estimated resource value
 */
export function calculateTotalResourceValue(tenement: any): number {
  if (!tenement.resourceEstimation) return 0;
  
  const { tonnage, grade, mineralType } = tenement.resourceEstimation;
  
  // Simplified calculation - in real app would use market prices
  const mineralPrices: { [key: string]: number } = {
    'gold': 1800, // USD per oz
    'silver': 24, // USD per oz
    'copper': 8000, // USD per tonne
    'iron': 100, // USD per tonne
    'zinc': 2800, // USD per tonne
    'lead': 2000, // USD per tonne
    'nickel': 16000, // USD per tonne
    'coal': 60, // USD per tonne
    'uranium': 50, // USD per pound
    'lithium': 15000, // USD per tonne
    'cobalt': 30000, // USD per tonne
    'manganese': 1500, // USD per tonne
    'rare_earths': 50000, // USD per tonne
    'default': 1000 // Default USD per tonne
  };
  
  const price = mineralPrices[mineralType.toLowerCase()] || mineralPrices.default;
  return tonnage * grade * price / 100; // Simplified calculation
}
